import React, { useCallback, useState } from "react";
import PaymentsLogo from "../../Assets/RegisterPage/PaymentsLogo.png";
import { useUserStore } from "../../Store/User";
import { validateEmail } from "../../Contracts/Common";

interface RegisterFormState {
  firstName: string;
  lastName: string;
  email: string;
}

const RegisterPage: React.FC = () => {
  const [inviteSent, setInviteSent] = useState(false);
  const [registerForm, setRegisterForm] = useState<RegisterFormState>({
    firstName: "",
    lastName: "",
    email: ""
  });
  const [errors, setErrors] = useState<Partial<RegisterFormState>>({});
  const handleRegisterFormChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: keyof RegisterFormState
  ) => {
    if (errors[key]) {
      setErrors({ ...errors, [key]: undefined });
    }
    setRegisterForm({ ...registerForm, [key]: e.target.value });
  };
  const { isLoading, requestSignup } = useUserStore();

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<RegisterFormState> = {};
    if (registerForm.firstName.length === 0) {
      newErrors.firstName = "First name is required";
    }
    if (registerForm.lastName.length === 0) {
      newErrors.lastName = "Last name is required";
    }
    if (registerForm.email.length === 0) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(registerForm.email)) {
      newErrors.email = "Invalid email address";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [registerForm]);

  const handleRegister = useCallback(async () => {
    if (!validateForm()) return;
    await requestSignup({
      email: registerForm.email,
      first_name: registerForm.firstName,
      last_name: registerForm.lastName
    });
    setInviteSent(true);
  }, [registerForm, requestSignup, validateForm]);

  return (
    <div
      className={`flex flex-col h-screen items-center bg-white-smoke pt-16 ${
        isLoading && "cursor-progress"
      }`}
    >
      <div className="w-[28rem] flex flex-col">
        <img src={PaymentsLogo} alt="Zomentum Payments Logo" className="w-60" />
        {!inviteSent ? (
          <div className="flex flex-col bg-white m-4">
            <div className="p-4 border-b border-gray-200 text-xl">Sign up</div>
            <div className="p-4 flex flex-col items-center">
              <div className="grid grid-cols-3 space-y-2 items-center gap-2">
                <label>First name</label>
                <input
                  className="col-span-2 px-4 py-2 border border-gray-200 rounded"
                  autoFocus
                  value={registerForm.firstName}
                  onChange={e => handleRegisterFormChange(e, "firstName")}
                  name="firstName"
                />
                {errors.firstName && (
                  <>
                    <div />
                    <p className="text-red-500 text-sm col-span-2">{errors.firstName}</p>
                  </>
                )}
                <label>Last name</label>
                <input
                  className="col-span-2 px-4 py-2 border border-gray-200 rounded"
                  value={registerForm.lastName}
                  name="lastName"
                  onChange={e => handleRegisterFormChange(e, "lastName")}
                />
                {errors.lastName && (
                  <>
                    <div />
                    <p className="text-red-500 text-sm col-span-2">{errors.lastName}</p>
                  </>
                )}
                <label>Email address</label>
                <input
                  className="col-span-2 px-4 py-2 border border-gray-200 rounded"
                  type="email"
                  value={registerForm.email}
                  onChange={e => handleRegisterFormChange(e, "email")}
                />
                {errors.email && (
                  <>
                    <div />
                    <p className="text-red-500 text-sm col-span-2">{errors.email}</p>
                  </>
                )}
              </div>
              <p className="mt-10 text-gray-700 text-center">
                By submitting, you are agreeing to our{" "}
                <a
                  href="https://www.zomentum.com/terms-and-conditions"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-blue-600"
                >
                  Terms
                </a>{" "}
                and{" "}
                <a
                  href="https://www.zomentum.com/privacy-policy"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-blue-600"
                >
                  Privacy Policy
                </a>
                .
              </p>
              <button
                className={`px-4 py-2 bg-blue-600 text-white rounded mt-6 ${
                  isLoading ? "cursor-not-allowed opacity-60" : "hover:bg-blue-400 "
                }`}
                disabled={isLoading}
                onClick={handleRegister}
              >
                Register
              </button>
              <p className="text-gray-700 text-center mt-4">
                Already have a user account?{" "}
                <a href={window.location.origin} className="text-blue-600">
                  Login
                </a>{" "}
                instead
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col bg-white m-4">
            <div className="p-4 border-b border-gray-200 text-xl">Verify your email</div>
            <div className="p-4 flex flex-col text-gray-700 space-y-4">
              <p>
                Zomentum Payments has sent a verification email to <b>{registerForm.email}</b>.
              </p>
              <p>Check you inbox and click on the link in the email to verify your address.</p>
              <p>
                Already have a user account?{" "}
                <a href={window.location.origin} className="text-blue-600">
                  Login
                </a>{" "}
                instead
              </p>
            </div>
          </div>
        )}
        <p className="text-xs m-4 p-4 self-center opacity-60">
          Zomentum payments is a service by{" "}
          <a href="https://zomentum.com" rel="noreferrer noopener" className="text-blue-600">
            Zomentum
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
